import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, UserPlus, Mail, Trash2, Clock, Check, UserIcon, Building2, Copy, Phone, BarChart3, SprayCan } from 'lucide-react';
import MobileNav from '../components/MobileNav';
import PullToRefresh from '../components/PullToRefresh';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { PropertyPickerModal } from '../components/PropertyPickerModal';
import { AssignedPropertiesDropdown } from '../components/AssignedPropertiesDropdown';
import {
  getMyCleaningCompany, managerInviteCleaner, managerRemoveCleaner,
  managerDeleteInvite, managerGetCleanerProperties, managerAssignCleanerProperties,
} from '../lib/api';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function ManagerTeamView() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [creating, setCreating] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [removeId, setRemoveId] = useState(null);
  const [deleteInviteId, setDeleteInviteId] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [savingAssign, setSavingAssign] = useState(false);
  const [inviteAssignNow, setInviteAssignNow] = useState(false);
  const [inviteProps, setInviteProps] = useState(new Set());
  const [showInvitePicker, setShowInvitePicker] = useState(false);

  const load = async (silent = false) => {
    try {
      const res = await getMyCleaningCompany();
      setCompany(res.data);
    } catch (err) {
      if (!silent) toast.error('Failed to load your cleaning team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useAutoRefresh(() => load(true));

  const handleInvite = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const propertyIds = inviteAssignNow ? Array.from(inviteProps) : [];
      const res = await managerInviteCleaner({ name, email, mobile: mobile.trim() || null, propertyIds });
      if (res.data.inviteLink) {
        setLastLink(res.data.inviteLink);
        try { await navigator.clipboard.writeText(res.data.inviteLink); } catch (e) {}
      }
      toast.success('Cleaner invited! An email was sent.');
      setName(''); setEmail(''); setMobile(''); setShowForm(false);
      setInviteAssignNow(false); setInviteProps(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite cleaner');
    } finally {
      setCreating(false);
    }
  };

  const handleRemove = async () => {
    try {
      await managerRemoveCleaner(removeId);
      toast.success('Cleaner removed');
      setRemoveId(null);
      load();
    } catch (err) {
      toast.error('Failed to remove cleaner');
    }
  };

  const handleDeleteInvite = async () => {
    try {
      await managerDeleteInvite(deleteInviteId);
      toast.success('Invite cancelled');
      setDeleteInviteId(null);
      load();
    } catch (err) {
      toast.error('Failed to cancel invite');
    }
  };

  const openAssign = async (cleaner) => {
    try {
      const res = await managerGetCleanerProperties(cleaner.id);
      setAssignModal({
        userId: cleaner.id,
        userName: cleaner.name || cleaner.email,
        properties: res.data.allProperties,
        selectedIds: new Set(res.data.assignedPropertyIds),
      });
    } catch (err) {
      toast.error('Failed to load properties');
    }
  };

  const toggleAssign = (pid) => {
    if (!assignModal) return;
    const s = new Set(assignModal.selectedIds);
    s.has(pid) ? s.delete(pid) : s.add(pid);
    setAssignModal({ ...assignModal, selectedIds: s });
  };

  const toggleInviteProp = (pid) => {
    const s = new Set(inviteProps);
    s.has(pid) ? s.delete(pid) : s.add(pid);
    setInviteProps(s);
  };

  const saveAssign = async () => {
    if (!assignModal) return;
    setSavingAssign(true);
    try {
      await managerAssignCleanerProperties(assignModal.userId, Array.from(assignModal.selectedIds));
      toast.success('Properties assigned');
      setAssignModal(null);
      load();
    } catch (err) {
      toast.error('Failed to assign properties');
    } finally {
      setSavingAssign(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)' }}>
        <div className="spinner-3d"></div>
      </div>
    );
  }

  const cleaners = company?.cleaners || [];
  const pending = (company?.pendingInvites || []).filter((i) => i.inviteType === 'cleaning_company_staff');
  const delegated = company?.delegatedProperties || [];

  return (
    <PullToRefresh onRefresh={() => load()}>
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)' }}>
      <MobileNav />
      <main className="flex-1 mobile-container pb-28 md:pb-8">
        <div className="flex items-center justify-between mb-6 animate-slide-in">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-2">My Team</h1>
            <p className="text-slate-600 font-medium">Manage your cleaners and assignments</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/manager/performance')} className="btn-3d h-14 w-14 sm:w-auto sm:px-5 rounded-2xl bg-white border-2 border-purple-200 text-purple-600 font-bold flex items-center justify-center gap-2" data-testid="manager-performance-button">
              <BarChart3 size={20} /> <span className="hidden sm:inline">Performance</span>
            </button>
            <button onClick={() => setShowForm(!showForm)} className="btn-3d btn-3d-accent h-14 w-14 sm:w-auto sm:px-6 rounded-2xl flex items-center justify-center gap-2" data-testid="invite-cleaner-button">
              <UserPlus size={20} /> <span className="hidden sm:inline">Add cleaner</span>
            </button>
          </div>
        </div>

        {/* Manager identity card (styled like the admin cleaning-company card) */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-3d p-5 mb-8" data-testid="manager-identity-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' }}>
                <SprayCan size={24} className="text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-900 leading-tight truncate">{company?.name}</h3>
                <p className="text-xs text-slate-500 mt-1 truncate">{company?.manager?.name}{company?.manager?.email ? ` · ${company.manager.email}` : ''}</p>
              </div>
            </div>
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-purple-700 border border-purple-200 flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #EEF2FF 100%)' }}
              data-testid="manager-badge"
            >
              <SprayCan size={12} /> Manager
            </span>
          </div>
          <div className="mt-4">
            <AssignedPropertiesDropdown properties={delegated} accent="purple" testid="manager-delegated-dropdown" />
          </div>
        </motion.div>

        {lastLink && (
          <div className="card-3d p-4 mb-6 border-2 border-emerald-200 bg-emerald-50">
            <p className="text-sm text-emerald-700 mb-2 font-medium">Cleaner invite link:</p>
            <div className="flex gap-2">
              <input readOnly value={lastLink} className="input-3d flex-1 bg-white text-xs font-mono" />
              <button onClick={() => { navigator.clipboard.writeText(lastLink); toast.success('Copied!'); }} className="btn-3d h-11 px-4 rounded-xl bg-emerald-500 text-white"><Copy size={16} /></button>
            </div>
            <button onClick={() => setLastLink(null)} className="mt-2 text-xs text-emerald-600">Dismiss</button>
          </div>
        )}

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="card-3d p-6 mb-8">
            <h3 className="text-2xl font-bold gradient-text mb-6">Invite a Cleaner</h3>
            <form onSubmit={handleInvite} className="space-y-4" data-testid="invite-cleaner-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Anna Bianchi" className="input-3d w-full" data-testid="cleaner-name-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="anna@example.com" className="input-3d w-full" data-testid="cleaner-email-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Mobile <span className="text-slate-400 font-normal">(optional)</span></label>
                <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+44 7123 456789" className="input-3d w-full" data-testid="cleaner-mobile-input" />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="cleaner-assign-section">
                <label className="flex items-center justify-between cursor-pointer">
                  <span>
                    <span className="block text-sm font-semibold text-slate-700">Assign properties now</span>
                    <span className="block text-xs text-slate-400 mt-0.5">Optional — you can also assign them later from the cleaner's card.</span>
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={inviteAssignNow}
                    onClick={() => setInviteAssignNow((v) => !v)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${inviteAssignNow ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    data-testid="cleaner-assign-toggle"
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${inviteAssignNow ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </label>
                {inviteAssignNow && (
                  <button
                    type="button"
                    onClick={() => setShowInvitePicker(true)}
                    className="mt-3 w-full h-11 rounded-xl bg-white border border-emerald-200 text-emerald-600 text-sm font-semibold hover:bg-emerald-50 transition-colors"
                    data-testid="cleaner-choose-properties"
                  >
                    {inviteProps.size > 0 ? `${inviteProps.size} propert${inviteProps.size === 1 ? 'y' : 'ies'} selected` : 'Choose properties'}
                  </button>
                )}
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={creating} className="btn-3d btn-3d-accent flex-1 h-12 rounded-2xl font-semibold" data-testid="send-cleaner-invite-button">
                  {creating ? 'Sending...' : 'Send Invite'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-3d btn-3d-outline h-12 px-6 rounded-2xl">Cancel</button>
              </div>
            </form>
          </motion.div>
        )}

        {pending.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2"><Mail size={24} className="text-blue-500" /> Pending Invites</h2>
            <div className="space-y-3">
              {pending.map((inv) => (
                <div key={inv.id} className="card-3d p-5 flex items-center justify-between" data-testid={`pending-invite-${inv.id}`}>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold text-slate-900">{inv.name}</h3>
                      <span className="badge-3d badge-warning"><Clock size={14} /> Pending</span>
                    </div>
                    <p className="text-sm text-slate-600">{inv.email}</p>
                  </div>
                  <button onClick={() => setDeleteInviteId(inv.id)} className="btn-3d h-10 px-4 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 border border-red-200" data-testid={`delete-invite-${inv.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2"><Users size={24} className="text-green-500" /> Cleaners ({cleaners.length})</h2>
          {cleaners.length === 0 ? (
            <div className="card-3d p-8 text-center">
              <Users size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No cleaners yet</h3>
              <p className="text-slate-500">Invite your first cleaner to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cleaners.map((m) => (
                <motion.div key={m.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-3d property-card-3d p-6" data-testid={`cleaner-card-${m.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                        {m.photoUrl ? <img src={m.photoUrl} alt={m.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <UserIcon size={24} className="text-white" />}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{m.name || 'Cleaner'}</h3>
                        <p className="text-xs text-slate-500">{m.email}</p>
                        {m.mobile && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Phone size={11} className="text-slate-400" /><a href={`tel:${m.mobile}`} className="hover:text-emerald-600">{m.mobile}</a>
                          </p>
                        )}
                      </div>
                    </div>
                    <button onClick={() => setRemoveId(m.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`remove-cleaner-${m.id}`}><Trash2 size={16} /></button>
                  </div>
                  <div className="mb-4">
                    <AssignedPropertiesDropdown
                      properties={m.assignedProperties || []}
                      testid={`assigned-properties-dropdown-${m.id}`}
                    />
                  </div>
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="badge-3d bg-green-50 text-green-700 border-green-200 text-xs"><Check size={12} /> Active</span>
                    <button onClick={() => openAssign(m)} className="btn-3d h-9 px-3 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200 text-sm font-semibold" data-testid={`assign-cleaner-${m.id}`}>
                      Manage Properties
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!removeId} onOpenChange={() => setRemoveId(null)}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl p-0 overflow-hidden max-w-md bg-white">
          <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4"><Trash2 size={32} className="text-white" /></div>
            <AlertDialogTitle className="text-2xl font-bold text-white m-0">Remove Cleaner?</AlertDialogTitle>
          </div>
          <div className="p-6 bg-white"><AlertDialogDescription className="text-center text-slate-600 text-base m-0">This removes their access and all property assignments.</AlertDialogDescription></div>
          <AlertDialogFooter className="p-6 pt-0 flex gap-3 bg-white">
            <AlertDialogCancel className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 bg-white text-slate-700">Keep</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 hover:opacity-90 border-0 text-white" data-testid="confirm-remove-cleaner">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteInviteId} onOpenChange={() => setDeleteInviteId(null)}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl p-0 overflow-hidden max-w-md bg-white">
          <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4"><Trash2 size={32} className="text-white" /></div>
            <AlertDialogTitle className="text-2xl font-bold text-white m-0">Cancel Invite?</AlertDialogTitle>
          </div>
          <div className="p-6 bg-white"><AlertDialogDescription className="text-center text-slate-600 text-base m-0">The person won't be able to join using this invite link.</AlertDialogDescription></div>
          <AlertDialogFooter className="p-6 pt-0 flex gap-3 bg-white">
            <AlertDialogCancel className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 bg-white text-slate-700">Keep Invite</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvite} className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 hover:opacity-90 border-0 text-white" data-testid="confirm-delete-invite">Cancel Invite</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AnimatePresence>
        {assignModal && (
          <PropertyPickerModal
            title="Assign Properties"
            subtitle={`to ${assignModal.userName}`}
            properties={assignModal.properties}
            selectedIds={assignModal.selectedIds}
            onToggle={toggleAssign}
            onSave={saveAssign}
            saving={savingAssign}
            onClose={() => setAssignModal(null)}
            emptyText="No delegated properties yet. Ask your client to delegate properties to your company."
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showInvitePicker && (
          <PropertyPickerModal
            title="Assign Properties"
            subtitle="to this new cleaner"
            properties={delegated}
            selectedIds={inviteProps}
            onToggle={toggleInviteProp}
            onSave={() => setShowInvitePicker(false)}
            saving={false}
            onClose={() => setShowInvitePicker(false)}
            emptyText="No delegated properties yet. Ask your client to delegate properties to your company."
          />
        )}
      </AnimatePresence>
    </div>
    </PullToRefresh>
  );
}
